import { useId } from 'react';

// The AirBridge letter mark — the blue gradient "A" with its swoosh, from the
// brand kit's `airbridge-a-mark.svg` (also kept beside this file as the source
// artefact).
//
// Inlined rather than `<img src=…>` for two reasons: the gradients need ids that
// cannot collide when the mark renders twice on one page (the rail and a hero,
// say), and an inline SVG inherits `currentColor` for a future single-colour
// variant. `useId()` namespaces the three gradients per instance — two marks
// sharing a gradient id is a real defect in SVG, and it renders as the second
// one silently taking the first one's fill.
//
// The mark carries NO wordmark text: "AirBridge" is typeset beside it (Inter
// 600) by whatever renders it, so the casing lives in one place.

export interface AirBridgeMarkProps {
  /** Rendered height in px. The viewBox is 172×127, so width follows. */
  size?: number;
  className?: string;
  /**
   * Accessible name. Defaults to empty, because the mark almost always sits
   * inside a control or heading that already carries one — a second name makes
   * a screen reader say it twice.
   */
  title?: string;
}

export default function AirBridgeMark({ size = 28, className, title }: AirBridgeMarkProps) {
  const uid = useId().replace(/:/g, '');
  const g1 = `ab-a-${uid}-1`;
  const g2 = `ab-a-${uid}-2`;
  const g3 = `ab-a-${uid}-3`;

  return (
    <svg
      viewBox="0 0 172 127"
      height={size}
      width={(size * 172) / 127}
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <linearGradient x1="1.754%" y1="-3.759%" x2="71.488%" y2="103.008%" id={g1}>
          <stop stopColor="#36B9FF" offset="0%" />
          <stop stopColor="#167DEB" offset="55%" />
          <stop stopColor="#0757C8" offset="100%" />
        </linearGradient>
        <linearGradient x1="19.965%" y1="100.604%" x2="75.293%" y2="0.403%" id={g2}>
          <stop stopColor="#1688F4" offset="0%" />
          <stop stopColor="#55B9FF" offset="52%" />
          <stop stopColor="#8AD5FF" offset="100%" />
        </linearGradient>
        <linearGradient x1="16.129%" y1="57.133%" x2="100%" y2="32.167%" id={g3}>
          <stop stopColor="#8EE7FF" offset="0%" />
          <stop stopColor="#36C6F4" offset="45%" />
          <stop stopColor="#12A7D9" offset="100%" />
        </linearGradient>
      </defs>
      <g fillRule="nonzero" transform="translate(0.4582, 0.9839)">
        <path
          d="M130.431662,54.6532987 L171.274542,125.51962 L120.574436,125.51962 L103.746166,76.1099799 L103.70611,75.9786937 C110.286968,69.4926386 119.481179,61.4612273 130.431662,54.6532987 Z M114.32214,26.7003394 L125.793587,46.6054448 L100.741587,66.3534448 L95.7858748,50.2591717 C93.559108,47.4309628 91.282019,45.5722283 88.9546078,44.6829681 C86.6271966,43.7937079 84.0395231,43.7937079 81.1915874,44.6829681 C82.1322118,34.9930751 84.7198852,28.9988656 88.9546078,26.7003394 C93.1893304,24.4018133 101.645175,24.4018133 114.32214,26.7003394 Z"
          fill={`url(#${g1})`}
        />
        <path
          d="M0,125.51962 L61.6290942,11.4259285 C75.4140197,-5.6039017 98.2517917,-1.90131058 107.574936,11.4259285 L123.258188,42.2006068 L104.420079,52.5494888 C99.714638,44.1393113 95.6711529,39.7109987 92.2896237,39.2645508 C88.9080944,38.818103 85.7217507,41.0686222 82.7305926,46.0161084 L43.5163591,125.51962 L0,125.51962 Z"
          fill={`url(#${g2})`}
        />
        <path
          d="M0,125.51962 C42,76.5196203 104.274542,33.7003394 171.274542,26.7003394 C151.623617,33.6873349 119.993224,51.6646829 89.5418139,77.7649051 C78.8504106,86.9286185 63.5085923,102.846857 43.5163591,125.51962 C23.5057976,125.51962 9.00034452,125.51962 0,125.51962 Z"
          fill={`url(#${g3})`}
        />
      </g>
    </svg>
  );
}
